import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'

import { BASE_URL } from '../constants'
import { PREFERENCES } from '../user/constants'

import { useUserPreference, useUserLocation } from '../user/hooks'
import { useToken } from '../auth/hooks'

import {
  fetchSuccess,
  fetchStart,
  fetchFail,
  fetchPalmettoIncidentsStart,
  fetchPalmettoIncidentsSuccess,
  fetchPalmettoIncidentsFail,
  fetchEventTypesStart,
  fetchEventTypesSuccess,
  fetchEventTypesFail,
  nextIncident,
  prevIncident,
  selectIncidents,
  clearSelection,
  hover,
  endHover,
  openForm,
  closeForm,
  sendToPalmetto,
  setEventType,
  setIncident,
  setTitle,
} from './actions'

import {
  getAllIncidentIds,
  getAllIncidents,
  getIncidentById,
  getSelectedIncidentIds,
  getAllSelectedIncidents,
  getSelectedCount,
  getCurrentSelectedIndex,
  getCurrentSelectedIncident,
  getIsLoading,
  getEventTypes,
  getPalmettoIncidents,
  getFormState,
  getIsFormOpen,
} from './selectors'

import { useCrimeCategories } from '../crimeCategories/hooks'
import useRecurrantFetch from '../hooks/useRecurrantFetch'

export const useIncidentsFetch = () => {
  const dispatch = useDispatch()
  return useRecurrantFetch(`${BASE_URL}api/palmetto/incidents`, {
    onStart: () => dispatch(fetchStart()),
    onSuccess: (incidents) => dispatch(fetchSuccess(incidents)),
    onError: (error) => dispatch(fetchFail(error)),
  })
}

export const usePalmettoIncidentsFetch = () => {
  const dispatch = useDispatch()
  return useRecurrantFetch(`${BASE_URL}api/palmetto/palmettoIncidents`, {
    onStart: () => dispatch(fetchPalmettoIncidentsStart()),
    onSuccess: (incidents) =>
      dispatch(fetchPalmettoIncidentsSuccess(incidents)),
    onError: (error) => dispatch(fetchPalmettoIncidentsFail(error)),
  })
}

export const usePalmettoIncidents = () => {
  const incidents = useSelector(getPalmettoIncidents)
  return incidents
}

export const useEventTypesFetch = () => {
  const dispatch = useDispatch()
  return useRecurrantFetch(`${BASE_URL}api/palmetto/eventTypes`, {
    onStart: () => dispatch(fetchEventTypesStart()),
    onSuccess: (types) => dispatch(fetchEventTypesSuccess(types)),
    onError: (error) => dispatch(fetchEventTypesFail(error)),
  })
}

export const useEventTypes = () => {
  const types = useSelector(getEventTypes)
  return types
}

// used by IncidentDetailsPanel
export const useClearIncidents = () => {
  const dispatch = useDispatch()
  const clear = useCallback(() => dispatch(clearSelection()), [dispatch])
  return clear
}

export const useIncidentsMap = () => {
  const dispatch = useDispatch()
  const selectedIds = useSelector(getSelectedIncidentIds)
  const current = useSelector(getCurrentSelectedIndex)
  const incidents = useSelector(getAllIncidents)
  const categories = useCrimeCategories()

  const select = useCallback(
    (incidentIds) => dispatch(selectIncidents(incidentIds)),
    [dispatch]
  )
  const clear = useCallback(() => dispatch(clearSelection()), [dispatch])
  const selected = useMemo(() => ({ ids: selectedIds, current }), [
    selectedIds,
    current,
  ])
  return [
    { incidents, categories, selected },
    { select, clear },
  ]
}

export const useSelectedIncidents = () => {
  const dispatch = useDispatch()
  const selected = useSelector(getAllSelectedIncidents)
  const current = useSelector(getCurrentSelectedIncident)

  const next = useCallback(() => dispatch(nextIncident()), [dispatch])
  const prev = useCallback(() => dispatch(prevIncident()), [dispatch])
  const clear = useCallback(() => dispatch(clearSelection()), [dispatch])

  return [
    { selected, current },
    { prev, next, clear },
  ]
}

export const useIncidentPager = () => {
  const dispatch = useDispatch()
  const incidentCount = useSelector(getSelectedCount)
  const currentIndex = useSelector(getCurrentSelectedIndex)

  const next = useCallback(() => dispatch(nextIncident()), [dispatch])
  const prev = useCallback(() => dispatch(prevIncident()), [dispatch])

  return [
    { page: currentIndex + 1, totalPages: incidentCount },
    { next, prev },
  ]
}

export const useSelectedIncident = () => useSelector(getCurrentSelectedIncident)

const EARTH_RADIUS = 6371 // in km
const getDistance = (a, b) => {
  const distance =
    Math.acos(
      Math.sin((b.latitude * Math.PI) / 180) *
        Math.sin((a.latitude * Math.PI) / 180) +
        Math.cos((b.latitude * Math.PI) / 180) *
          Math.cos((a.latitude * Math.PI) / 180) *
          Math.cos(((a.longitude - b.longitude) * Math.PI) / 180)
    ) * EARTH_RADIUS
  return distance // in km
}

export const useIncidentList = () => {
  const sortOrder = useUserPreference(PREFERENCES.INCIDENT_DISPLAY_ORDER)
  const location = useUserLocation()
  const unsortedIncidentIds = useSelector(getAllIncidentIds)
  const byId = useSelector(getIncidentById)
  const isLoading = useSelector(getIsLoading)

  let sortFunc = undefined
  if (sortOrder === 'location') {
    sortFunc = (a, b) =>
      getDistance(location, a) < getDistance(location, b) ? 1 : -1
  } else {
    // by time
    sortFunc = (a, b) => {
      const A = byId(a)
      const B = byId(b)
      if (moment(A.incidentDate).isSame(B.incidentDate))
        return A.incidentNumber < B.incidentNumber ? -1 : 1
      return moment(A.incidentDate).isBefore(B.incidentDate) ? 1 : -1
    }
  }

  const incidentIds = unsortedIncidentIds.slice().sort(sortFunc)

  return [{ incidentIds, isLoading }]
}

export const useIncident = (incidentId) => {
  const dispatch = useDispatch()
  const incident = useSelector(getIncidentById)(incidentId)

  const select = useCallback(() => dispatch(selectIncidents([incidentId])), [
    dispatch,
    incidentId,
  ])

  const onHover = useCallback(() => dispatch(hover(incidentId)), [
    dispatch,
    incidentId,
  ])
  const onHoverEnd = useCallback(() => dispatch(endHover(incidentId)), [
    dispatch,
    incidentId,
  ])

  return [incident, { select, onHover, onHoverEnd }]
}

export const useSendToPalmettoButton = () => {
  const dispatch = useDispatch()
  const open = useCallback(() => {
    dispatch(openForm())
  }, [dispatch])
  return open
}

export const useAddIncidentFormIsOpen = () => {
  const open = useSelector(getIsFormOpen)
  return open
}

export const useAddIncidentForm = () => {
  const dispatch = useDispatch()
  const token = useToken()
  const formState = useSelector(getFormState)
  const { incidentNumber } = useSelectedIncident()
  const [validationErrors, setErrors] = useState([])

  const actions = useMemo(() => {
    return {
      close: () => dispatch(closeForm()),
      send: () => {
        fetch(`${BASE_URL}api/palmetto`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            palmettoIncidentName: formState.incident,
            p1IncidentNumber: incidentNumber,
            title: formState.title,
            eventType: formState.eventType,
          }),
        })
        dispatch(sendToPalmetto(incidentNumber))
      },
      setTitle: (title) => dispatch(setTitle(title)),
      setEventType: (eventType) => dispatch(setEventType(eventType)),
      setIncident: (incident) => dispatch(setIncident(incident)),
    }
  }, [dispatch, incidentNumber, formState, token])

  return [{ ...formState, validationErrors }, actions]
}
